import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../../css/authors.css';

const AUTHORS = [
  {
    name: 'Maya Okafor',
    title: 'Senior Hockey Analyst',
    bio: 'Maya covers team dynamics and standings with a focus on tactics and systemic trends in women\'s hockey.',
    avatar: 'https://unsupervisedbias.com/images/authors/maya-okafor.jpg',
    sql: [
      {
        label: 'Standings — Best/Worst Goal Differential',
        query: `SELECT t.name, t.abbreviation,
  SUM(CASE WHEN g.home_team_id=t.id THEN g.home_score ELSE g.away_score END) AS gf,
  SUM(CASE WHEN g.home_team_id=t.id THEN g.away_score ELSE g.home_score END) AS ga,
  COUNT(*) AS gp
FROM pwhl_teams t
JOIN games g ON (g.home_team_id=t.id OR g.away_team_id=t.id)
WHERE g.status='final' AND g.season=:s
GROUP BY t.id, t.name, t.abbreviation
ORDER BY (gf - ga) DESC
LIMIT 1;`,
      },
      {
        label: 'Team Defense — Fewest Goals Allowed per Game',
        query: `SELECT t.name, t.abbreviation,
  SUM(CASE WHEN g.home_team_id=t.id THEN g.away_score ELSE g.home_score END) AS ga,
  COUNT(*) AS gp
FROM pwhl_teams t
JOIN games g ON (g.home_team_id=t.id OR g.away_team_id=t.id)
WHERE g.status='final' AND g.season=:s
GROUP BY t.id, t.name, t.abbreviation
ORDER BY (ga::float / COUNT(*)) ASC
LIMIT 1;`,
      },
      {
        label: 'Discipline — Most Penalty Minutes (Team)',
        query: `SELECT t.name, t.abbreviation,
  SUM(ps.penalty_minutes) AS pim
FROM pwhl_teams t
JOIN players p ON p.pwhl_team_id=t.id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true AND p.position != 'G'
GROUP BY t.id, t.name, t.abbreviation
ORDER BY pim DESC
LIMIT 1;`,
      },
      {
        label: 'Team Dominance — Most Blowout Wins (3+ Goals)',
        query: `SELECT t.name, t.abbreviation,
  SUM(CASE
    WHEN (g.home_team_id=t.id AND g.home_score - g.away_score >= 3)
      OR (g.away_team_id=t.id AND g.away_score - g.home_score >= 3)
    THEN 1 ELSE 0 END) AS blowouts,
  COUNT(*) AS gp
FROM pwhl_teams t
JOIN games g ON (g.home_team_id=t.id OR g.away_team_id=t.id)
WHERE g.status='final' AND g.season=:s
GROUP BY t.id, t.name, t.abbreviation
ORDER BY blowouts DESC
LIMIT 1;`,
      },
    ],
  },
  {
    name: 'Sarah Chen',
    title: 'Advanced Metrics Writer',
    bio: 'Sarah specializes in advanced statistics and data-driven storytelling in women\'s hockey.',
    avatar: 'https://unsupervisedbias.com/images/authors/sarah-chen.jpg',
    sql: [
      {
        label: 'Shooting % Leader (min 20 shots)',
        query: `SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  ps.goals, ps.shots,
  ROUND((ps.goals::float / ps.shots * 100)::numeric, 1) AS sh_pct
FROM players p
JOIN pwhl_teams t ON t.id=p.pwhl_team_id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true
  AND p.position != 'G' AND ps.shots >= 20 AND ps.goals > 0
ORDER BY (ps.goals::float / ps.shots) DESC
LIMIT 1;`,
      },
      {
        label: 'Two-Way Play — Best Plus/Minus (min 10 GP)',
        query: `SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  ps.plus_minus, ps.games_played
FROM players p
JOIN pwhl_teams t ON t.id=p.pwhl_team_id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true
  AND p.position != 'G' AND ps.games_played >= 10
  AND ps.plus_minus IS NOT NULL
ORDER BY ps.plus_minus DESC
LIMIT 1;`,
      },
      {
        label: 'Special Teams — Faceoff % Leader (min 50 attempts)',
        query: `SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  ps.faceoffs_won, ps.faceoffs_total,
  ROUND((ps.faceoffs_won::float / ps.faceoffs_total * 100)::numeric, 1) AS fo_pct
FROM players p
JOIN pwhl_teams t ON t.id=p.pwhl_team_id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true
  AND ps.faceoffs_total >= 50
ORDER BY (ps.faceoffs_won::float / ps.faceoffs_total) DESC
LIMIT 1;`,
      },
      {
        label: 'Fantasy Points Leader (min 10 GP)',
        query: `SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  ps.fantasy_points, ps.games_played
FROM players p
JOIN pwhl_teams t ON t.id=p.pwhl_team_id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true
  AND ps.fantasy_points IS NOT NULL AND ps.games_played >= 10
ORDER BY ps.fantasy_points DESC
LIMIT 1;`,
      },
    ],
  },
  {
    name: 'Jen Kowalski',
    title: 'Player Features Writer',
    bio: 'Jen focuses on the human stories behind the statistics — careers, journeys, and breakthrough moments.',
    avatar: 'https://unsupervisedbias.com/images/authors/jen-kowalski.jpg',
    sources: [
      {
        type: 'social',
        label: 'Player Social Accounts to Monitor',
        accounts: [
          // ── Boston Fleet ──────────────────────────────────────────────
          { name: 'Hilary Knight', team: 'SEA', platform: 'X', handle: '@HilaryKnight77' },
          { name: 'Hilary Knight', team: 'SEA', platform: 'Instagram', handle: '@hilaryknight' },
          { name: 'Megan Keller', team: 'BOS', platform: 'X', handle: '@MeganKeller' },
          { name: 'Megan Keller', team: 'BOS', platform: 'Instagram', handle: '@megankeller18' },
          { name: 'Haley Winn', team: 'BOS', platform: 'X', handle: '@HaleyWinn17' },
          { name: 'Loren Gabel', team: 'BOS', platform: 'X', handle: '@LorenGabel' },
          { name: 'Alina Müller', team: 'BOS', platform: 'Instagram', handle: '@alinamueller14' },
          { name: 'Susanna Tapani', team: 'BOS', platform: 'X', handle: '@SusannaTapani' },
          { name: 'Susanna Tapani', team: 'BOS', platform: 'Instagram', handle: '@susannatapani' },
          { name: 'Aerin Frankel', team: 'BOS', platform: 'X', handle: '@AerinFrankel' },
          // ── Minnesota Frost ───────────────────────────────────────────
          { name: 'Taylor Heise', team: 'MIN', platform: 'X', handle: '@TaylorHeise15' },
          { name: 'Kendall Coyne Schofield', team: 'MIN', platform: 'X', handle: '@KendallCoyne' },
          { name: 'Kendall Coyne Schofield', team: 'MIN', platform: 'Instagram', handle: '@kendallcoyne' },
          { name: 'Kelly Pannek', team: 'MIN', platform: 'X', handle: '@KellyPannek' },
          { name: 'Grace Zumwinkle', team: 'MIN', platform: 'X', handle: '@GraceZumwinkle' },
          // ── Montréal Victoire ─────────────────────────────────────────
          { name: 'Marie-Philip Poulin', team: 'MTL', platform: 'X', handle: '@MPPoulin' },
          { name: 'Ann-Sophie Bettez', team: 'MTL', platform: 'Instagram', handle: '@asbettez' },
          { name: 'Dara Greig', team: 'MTL', platform: 'Instagram', handle: '@dara_greig' },
          { name: 'Laura Stacey', team: 'MTL', platform: 'X', handle: '@LauraStacey26' },
          // ── New York Sirens ───────────────────────────────────────────
          { name: 'Sarah Fillier', team: 'NYR', platform: 'X', handle: '@sarahfillier' },
          { name: 'Micah Zandee-Hart', team: 'NYR', platform: 'X', handle: '@MicahZandeeHart' },
          { name: 'Kristýna Kaltounková', team: 'NYR', platform: 'Instagram', handle: '@kaltounkovak' },
          // ── Ottawa Charge ─────────────────────────────────────────────
          { name: 'Brianne Jenner', team: 'OTT', platform: 'X', handle: '@BrianneJenner' },
          { name: 'Emily Clark', team: 'OTT', platform: 'X', handle: '@EmilyClark16' },
          { name: 'Natalie Spooner', team: 'TOR', platform: 'X', handle: '@NatSpooner' },
          // ── Toronto Sceptres ──────────────────────────────────────────
          { name: 'Jesse Compher', team: 'TOR', platform: 'X', handle: '@JesseCompher' },
          { name: 'Daryl Watts', team: 'TOR', platform: 'Instagram', handle: '@darylwatts' },
          { name: 'Blayre Turnbull', team: 'TOR', platform: 'X', handle: '@BlayreturnbullB' },
          // ── Seattle Freeze ────────────────────────────────────────────
          { name: 'Alex Carpenter', team: 'SEA', platform: 'Instagram', handle: '@alex_carpenter_' },
          { name: 'Mikyla Grant-Mentis', team: 'SEA', platform: 'X', handle: '@mGrantMentis' },
          // ── Vancouver Victoire ────────────────────────────────────────
          { name: 'Sarah Nurse', team: 'VAN', platform: 'X', handle: '@sarahnurse16' },
          { name: 'Sarah Nurse', team: 'VAN', platform: 'Instagram', handle: '@sarahnurse16' },
          { name: 'Emerance Maschmeyer', team: 'VAN', platform: 'X', handle: '@GoalieMasch' },
          { name: 'Madison Samoskevich', team: 'VAN', platform: 'Instagram', handle: '@madison.samoskevich' },
        ],
      },
      {
        type: 'sql',
        label: 'Player-Focused Articles (news_articles with player headshot)',
        query: `SELECT id, title, LEFT(summary, 120) AS summary_preview,
  date, player_image_url
FROM news_articles
WHERE player_image_url LIKE '%leaguestat%'
  OR player_image_url LIKE '%240x240%'
ORDER BY scraped_at DESC
LIMIT 20;`,
      },
      {
        type: 'sql',
        label: 'Search Articles Mentioning a Specific Player',
        query: `SELECT id, title, LEFT(summary, 150) AS summary_preview, date
FROM news_articles
WHERE title ILIKE '%:player_name%'
   OR summary ILIKE '%:player_name%'
ORDER BY scraped_at DESC
LIMIT 10;`,
      },
    ],
  },
  {
    name: 'Priya Sharma',
    title: 'Goaltending & Defense Correspondent',
    bio: 'Priya is the go-to authority on goaltending mechanics, shot-blocking, and defensive systems.',
    avatar: 'https://unsupervisedbias.com/images/authors/priya-sharma.jpg',
    sql: [
      {
        label: 'Goaltending — Best Goals-Against Average (min 5 GP)',
        query: `SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  ps.goals_against_average, ps.games_played, ps.wins, ps.shutouts
FROM players p
JOIN pwhl_teams t ON t.id=p.pwhl_team_id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true
  AND p.position='G' AND ps.games_played >= 5
  AND ps.goals_against_average IS NOT NULL AND ps.goals_against_average > 0
ORDER BY ps.goals_against_average ASC
LIMIT 1;`,
      },
      {
        label: 'Goaltending — Rolling SV% Last 5 Starts (min .920)',
        query: `WITH ranked AS (
  SELECT ps.player_id,
    COALESCE(ps.saves, 0) AS saves,
    COALESCE(ps.shots_against, 0) AS shots_against,
    ROW_NUMBER() OVER (PARTITION BY ps.player_id ORDER BY g.game_date DESC) AS rn
  FROM player_stats ps
  JOIN games g ON g.id = ps.game_id
  WHERE ps.is_season_total = false AND ps.season = :s
)
SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  SUM(r.saves) AS saves, SUM(r.shots_against) AS shots_against, COUNT(*) AS gp,
  ROUND((SUM(r.saves)::float / NULLIF(SUM(r.shots_against), 0))::numeric, 3) AS sv_pct
FROM ranked r
JOIN players p ON p.id = r.player_id
JOIN pwhl_teams t ON t.id = p.pwhl_team_id
WHERE r.rn <= 5 AND p.position = 'G'
GROUP BY p.id, p.first_name, p.last_name, t.abbreviation
HAVING COUNT(*) >= 4
  AND (SUM(r.saves)::float / NULLIF(SUM(r.shots_against), 0)) >= 0.920
ORDER BY sv_pct DESC
LIMIT 1;`,
      },
      {
        label: 'Defense — Blocked Shots Leader',
        query: `SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  ps.blocks, ps.games_played
FROM players p
JOIN pwhl_teams t ON t.id=p.pwhl_team_id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true
  AND p.position != 'G' AND ps.blocks IS NOT NULL AND ps.blocks > 0
ORDER BY ps.blocks DESC
LIMIT 1;`,
      },
      {
        label: 'Physical Play — Hit Leader',
        query: `SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  ps.hits, ps.games_played
FROM players p
JOIN pwhl_teams t ON t.id=p.pwhl_team_id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true
  AND p.position != 'G' AND ps.hits IS NOT NULL AND ps.hits > 0
ORDER BY ps.hits DESC
LIMIT 1;`,
      },
    ],
  },
  {
    name: 'Alexis Rivera',
    title: 'Fantasy Hockey Editor',
    bio: 'Alexis covers the fantasy angle on every PWHL storyline, with an eye for value picks and breakout potential.',
    avatar: 'https://unsupervisedbias.com/images/authors/alexis-rivera.jpg',
    sql: [
      {
        label: 'Fantasy Points Leader — Season Total (min 10 GP)',
        query: `SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  ps.fantasy_points, ps.games_played, p.position
FROM players p
JOIN pwhl_teams t ON t.id=p.pwhl_team_id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true
  AND ps.fantasy_points IS NOT NULL AND ps.games_played >= 10
ORDER BY ps.fantasy_points DESC
LIMIT 1;`,
      },
      {
        label: 'Rolling — Best Fantasy Pickup (Last 10 Games)',
        query: `WITH ranked AS (
  SELECT ps.player_id, ps.fantasy_points AS fp,
    ROW_NUMBER() OVER (PARTITION BY ps.player_id ORDER BY g.game_date DESC) AS rn
  FROM player_stats ps
  JOIN games g ON g.id = ps.game_id
  WHERE ps.is_season_total = false AND ps.season = :s
    AND ps.fantasy_points IS NOT NULL
)
SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  SUM(r.fp) AS fp, COUNT(*) AS gp
FROM ranked r
JOIN players p ON p.id = r.player_id
JOIN pwhl_teams t ON t.id = p.pwhl_team_id
WHERE r.rn <= 10
GROUP BY p.id, p.first_name, p.last_name, t.abbreviation
HAVING COUNT(*) >= 8
ORDER BY SUM(r.fp) DESC
LIMIT 1;`,
      },
      {
        label: 'Scoring — Goals Leader',
        query: `SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  ps.goals, ps.games_played
FROM players p
JOIN pwhl_teams t ON t.id=p.pwhl_team_id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true
  AND p.position != 'G'
ORDER BY ps.goals DESC
LIMIT 1;`,
      },
    ],
  },
  {
    name: 'Danielle Tremblay',
    title: 'Form & Special Teams Analyst',
    bio: 'Danielle brings a French-Canadian hockey perspective to form analysis, momentum shifts, and special teams.',
    avatar: 'https://unsupervisedbias.com/images/authors/danielle-tremblay.jpg',
    sql: [
      {
        label: 'Recent Form — Best/Worst Point % Last 5 Games',
        query: `WITH last5 AS (
  SELECT t.id, t.name, t.abbreviation, g.game_date,
    CASE WHEN (g.home_team_id=t.id AND g.home_score > g.away_score)
              OR (g.away_team_id=t.id AND g.away_score > g.home_score)
         THEN 2
         WHEN g.is_overtime OR g.is_shootout THEN 1
         ELSE 0 END AS pts
  FROM pwhl_teams t
  JOIN games g ON (g.home_team_id=t.id OR g.away_team_id=t.id)
  WHERE g.status='final'
),
ranked AS (
  SELECT *, ROW_NUMBER() OVER (PARTITION BY id ORDER BY game_date DESC) AS rn
  FROM last5
)
SELECT id, name, abbreviation,
  SUM(pts) AS earned, COUNT(*)*2 AS possible, COUNT(*) AS gp
FROM ranked WHERE rn <= 5
GROUP BY id, name, abbreviation
HAVING COUNT(*) >= 5
ORDER BY (SUM(pts)::float / (COUNT(*)*2)) DESC
LIMIT 1;`,
      },
      {
        label: 'Home/Away — Best Home & Road Records',
        query: `-- Best home record
SELECT t.name, t.abbreviation,
  SUM(CASE WHEN g.home_score > g.away_score THEN 1 ELSE 0 END) AS hw,
  COUNT(*) AS hgp
FROM pwhl_teams t
JOIN games g ON g.home_team_id=t.id
WHERE g.status='final' AND g.season=:s
GROUP BY t.id, t.name, t.abbreviation
HAVING COUNT(*) >= 5
ORDER BY (hw::float / COUNT(*)) DESC
LIMIT 1;

-- Best road record
SELECT t.name, t.abbreviation,
  SUM(CASE WHEN g.away_score > g.home_score THEN 1 ELSE 0 END) AS aw,
  COUNT(*) AS agp
FROM pwhl_teams t
JOIN games g ON g.away_team_id=t.id
WHERE g.status='final' AND g.season=:s
GROUP BY t.id, t.name, t.abbreviation
HAVING COUNT(*) >= 5
ORDER BY (aw::float / COUNT(*)) DESC
LIMIT 1;`,
      },
      {
        label: 'Clutch Play — Best Overtime/Shootout Record',
        query: `SELECT t.name, t.abbreviation,
  SUM(CASE WHEN (g.is_overtime OR g.is_shootout)
            AND ((g.home_team_id=t.id AND g.home_score > g.away_score)
                 OR (g.away_team_id=t.id AND g.away_score > g.home_score))
       THEN 1 ELSE 0 END) AS ot_wins,
  SUM(CASE WHEN g.is_overtime OR g.is_shootout THEN 1 ELSE 0 END) AS ot_gp
FROM pwhl_teams t
JOIN games g ON (g.home_team_id=t.id OR g.away_team_id=t.id)
WHERE g.status='final' AND g.season=:s
GROUP BY t.id, t.name, t.abbreviation
HAVING SUM(CASE WHEN g.is_overtime OR g.is_shootout THEN 1 ELSE 0 END) >= 3
ORDER BY ot_wins DESC
LIMIT 1;`,
      },
    ],
  },
  {
    name: 'Kate Morrison',
    title: 'League Strategy & Roster Correspondent',
    bio: 'Kate breaks down team construction, line combinations, and the strategic roster decisions shaping the PWHL.',
    avatar: 'https://unsupervisedbias.com/images/authors/kate-morrison.jpg',
    sql: [
      {
        label: 'Team Dominance — Most Goals Scored Last 10 Games',
        query: `WITH last10 AS (
  SELECT t.id, t.name, t.abbreviation, g.game_date,
    CASE WHEN g.home_team_id=t.id THEN g.home_score ELSE g.away_score END AS gf
  FROM pwhl_teams t
  JOIN games g ON (g.home_team_id=t.id OR g.away_team_id=t.id)
  WHERE g.status='final'
),
ranked AS (
  SELECT *, ROW_NUMBER() OVER (PARTITION BY id ORDER BY game_date DESC) AS rn
  FROM last10
)
SELECT id, name, abbreviation,
  SUM(gf) AS goals, COUNT(*) AS gp
FROM ranked WHERE rn <= 10
GROUP BY id, name, abbreviation
HAVING COUNT(*) >= 8
ORDER BY SUM(gf) DESC
LIMIT 1;`,
      },
      {
        label: 'Team Defense — Fewest Goals Allowed Last 10 Games',
        query: `WITH last10 AS (
  SELECT t.id, t.name, t.abbreviation, g.game_date,
    CASE WHEN g.home_team_id=t.id THEN g.away_score ELSE g.home_score END AS ga
  FROM pwhl_teams t
  JOIN games g ON (g.home_team_id=t.id OR g.away_team_id=t.id)
  WHERE g.status='final'
),
ranked AS (
  SELECT *, ROW_NUMBER() OVER (PARTITION BY id ORDER BY game_date DESC) AS rn
  FROM last10
)
SELECT id, name, abbreviation,
  SUM(ga) AS goals_against, COUNT(*) AS gp
FROM ranked WHERE rn <= 10
GROUP BY id, name, abbreviation
HAVING COUNT(*) >= 8
ORDER BY SUM(ga) ASC
LIMIT 1;`,
      },
    ],
  },
  {
    name: 'Rhiannon Sykes',
    title: 'PWHL Insider & Breaking News Correspondent',
    bio: "Rhiannon is UnsupervisedBias's go-to source for breaking PWHL news, transactions, and off-ice storylines.",
    avatar: 'https://unsupervisedbias.com/images/authors/rhiannon-sykes.jpg',
    sql: [
      {
        label: 'Hot Players — Points Leaders Last 10 Games (min 8 GP)',
        query: `WITH ranked AS (
  SELECT ps.player_id,
    COALESCE(ps.goals, 0) + COALESCE(ps.assists, 0) AS pts,
    ROW_NUMBER() OVER (PARTITION BY ps.player_id ORDER BY g.game_date DESC) AS rn
  FROM player_stats ps
  JOIN games g ON g.id = ps.game_id
  WHERE ps.is_season_total = false AND ps.season = :s
)
SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  SUM(r.pts) AS pts, COUNT(*) AS gp
FROM ranked r
JOIN players p ON p.id = r.player_id
JOIN pwhl_teams t ON t.id = p.pwhl_team_id
WHERE r.rn <= 10 AND p.position != 'G'
GROUP BY p.id, p.first_name, p.last_name, t.abbreviation
HAVING COUNT(*) >= 8
ORDER BY SUM(r.pts) DESC
LIMIT 1;`,
      },
      {
        label: 'Interesting Stories — Lone Wolf (Best Player on Worst Team)',
        query: `WITH team_records AS (
  SELECT t.id, t.name, t.abbreviation,
    SUM(CASE WHEN (g.home_team_id=t.id AND g.home_score > g.away_score)
                  OR (g.away_team_id=t.id AND g.away_score > g.home_score)
             THEN 1 ELSE 0 END)::float / COUNT(*) AS win_pct
  FROM pwhl_teams t
  JOIN games g ON (g.home_team_id=t.id OR g.away_team_id=t.id)
  WHERE g.status='final' AND g.season=:s
  GROUP BY t.id, t.name, t.abbreviation
),
worst_team AS (
  SELECT id, name, abbreviation FROM team_records ORDER BY win_pct ASC LIMIT 1
)
SELECT p.first_name||' '||p.last_name AS pname,
  wt.name AS tname, wt.abbreviation,
  ps.points, ps.goals, ps.assists, ps.games_played
FROM worst_team wt
JOIN players p ON p.pwhl_team_id=wt.id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true
  AND p.position != 'G' AND ps.games_played >= 10
ORDER BY ps.points DESC
LIMIT 1;`,
      },
      {
        label: 'Interesting Stories — Record Scoring Pace',
        query: `SELECT p.first_name||' '||p.last_name AS name, t.abbreviation,
  ps.points, ps.goals, ps.games_played,
  ROUND((ps.points::float / ps.games_played * 26)::numeric, 0) AS pace_26
FROM players p
JOIN pwhl_teams t ON t.id=p.pwhl_team_id
JOIN player_stats ps ON ps.player_id=p.id
WHERE ps.season=:s AND ps.is_season_total=true
  AND p.position != 'G' AND ps.games_played BETWEEN 8 AND 20
ORDER BY (ps.points::float / ps.games_played) DESC
LIMIT 1;`,
      },
    ],
  },
];

const PLATFORM_COLORS = {
  X: '#1a1a1a',
  Instagram: '#c13584',
};

const DataSources = ({ sql, sources }) => {
  const [open, setOpen] = useState(false);

  // Normalise: authors with raw `sql` array use the old format
  const items = sources || (sql ? sql.map(q => ({ type: 'sql', label: q.label, query: q.query })) : []);
  const count = items.length;
  const label = sources ? 'Data Sources' : 'SQL Queries';

  return (
    <div className="author-sql-section">
      <button className="sql-toggle" onClick={() => setOpen(o => !o)}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points={open ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}></polyline>
        </svg>
        {open ? `Hide ${label}` : `Show ${label}`} ({count})
      </button>
      {open && (
        <div className="sql-blocks">
          {items.map((item, i) => (
            <div key={i} className="sql-block">
              <div className="sql-label">{item.label}</div>
              {item.type === 'social' ? (
                <div className="social-accounts">
                  {item.accounts.map((a, j) => (
                    <div key={j} className="social-row">
                      <span className="social-name">{a.name}</span>
                      <span className="social-team">{a.team}</span>
                      <span
                        className="social-platform"
                        style={{ background: PLATFORM_COLORS[a.platform] || '#333' }}
                      >
                        {a.platform}
                      </span>
                      <span className="social-handle">{a.handle}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <pre className="sql-code"><code>{item.query}</code></pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Authors = () => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user?.is_staff) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="authors-page">
      <div className="authors-hero">
        <h1>Meet Our Writers</h1>
        <p className="authors-subtitle">
          The analysts and correspondents behind UnsupervisedBias PWHL coverage.
        </p>
      </div>

      <div className="authors-grid">
        {AUTHORS.map((author) => (
          <div className="author-card" key={author.name}>
            <div className="author-avatar-wrap">
              <img
                src={author.avatar}
                alt={author.name}
                className="author-avatar"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="author-avatar-fallback" style={{ display: 'none' }}>
                {author.name.charAt(0)}
              </div>
            </div>
            <div className="author-info">
              <h2 className="author-name">{author.name}</h2>
              <p className="author-title">{author.title}</p>
              <p className="author-bio">{author.bio}</p>
              {(author.sql || author.sources) && <DataSources sql={author.sql} sources={author.sources} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Authors;
